import {useMutation} from '@apollo/client';
import {useCallback, useState} from 'react';
import {useDispatch} from 'react-redux';
import qualityCheckBusPartnerCostEntryYearMutation from '../graphql/qualityCheckBusPartnerCostEntryYearMutation';
import {qualityCheckedBusPartnerCostEntryYear} from '../store/slice';
import {useBusPartnerCostsQueryParams} from './useBusPartnerCostsQueryParams';

interface UpdateQualityCheckMutationVars {
  busPartnerId: string;
  year: number;
  qualityChecked: boolean;
  withValidation: boolean;
}

interface UpdateQualityCheckMutationResponse {
  qualityCheckBusPartnerCostEntryYear: {
    year: number;
    qualityChecked: boolean;
    qualityCheckedLastEditedAt: string;
  };
}

export const useUpdateBusPartnerQualityCheck = (
  ignoreValidationConfirmation: () => Promise<boolean>
) => {
  const dispatch = useDispatch();

  const {busPartnerId, year} = useBusPartnerCostsQueryParams();

  const [qualityChecked, setQualityChecked] = useState<boolean | null>(null);

  const [updateQualityCheckWithValidation] =
    useMutation<UpdateQualityCheckMutationResponse>(
      qualityCheckBusPartnerCostEntryYearMutation,
      {
        variables: {
          busPartnerId,
          year,
          qualityChecked,
          withValidation: true
        } as Partial<UpdateQualityCheckMutationVars>,
        onCompleted: ({qualityCheckBusPartnerCostEntryYear}) => {
          updateStore(qualityCheckBusPartnerCostEntryYear);
        },
        onError: async error => {
          const onlyValidationErrors = error.graphQLErrors.every(
            ({extensions}) =>
              extensions?.errorType === 'InvalidBusPartnerCostDataError' ||
              extensions?.errorType === 'InvalidBusPartnerBusTypeCostDataError'
          );

          if (onlyValidationErrors) {
            const confirmed = await ignoreValidationConfirmation();

            if (confirmed)
              updateQualityCheckWithoutValidation({
                variables: {qualityChecked}
              });
          } else {
            console.error('Error while updating quality check', error);
          }
        }
      }
    );

  const [updateQualityCheckWithoutValidation] =
    useMutation<UpdateQualityCheckMutationResponse>(
      qualityCheckBusPartnerCostEntryYearMutation,
      {
        variables: {
          busPartnerId,
          year,
          qualityChecked,
          withValidation: false
        } as Partial<UpdateQualityCheckMutationVars>,
        onCompleted: ({qualityCheckBusPartnerCostEntryYear}) => {
          updateStore(qualityCheckBusPartnerCostEntryYear);
        },
        onError: error => {
          console.error(
            'Error while updating quality check without validation',
            error
          );
        }
      }
    );

  const updateStore = useCallback(
    qualityCheckResult =>
      dispatch(qualityCheckedBusPartnerCostEntryYear(qualityCheckResult)),
    [dispatch]
  );

  const updateQualityCheck = useCallback(
    (qualityChecked: boolean) => {
      setQualityChecked(qualityChecked);
      updateQualityCheckWithValidation({variables: {qualityChecked}});
    },
    [setQualityChecked, updateQualityCheckWithValidation]
  );

  return {updateQualityCheck};
};
